import { type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Badge } from "../../feedback/Badge";

export type CommandMenuTone = "neutral" | "primary" | "success" | "warning" | "danger" | "info";

export type CommandMenuItem = {
  /** Stable id for the item. */
  id: string;
  /** Primary label, e.g. a command name. */
  label: ReactNode;
  /** Secondary description line. */
  description?: ReactNode;
  /** Leading icon node, rendered inside a tinted square. */
  icon?: ReactNode;
  /** Extra text used for filtering (in addition to a string `label`). */
  keywords?: string;
  /** Optional trailing status badge. */
  status?: { label: ReactNode; tone?: CommandMenuTone };
  disabled?: boolean;
};

export type CommandMenuProps = {
  /** Items to render. */
  items: CommandMenuItem[];
  /** Current filter query. When set and `filter` is not false, items are filtered. */
  query?: string;
  /** Optional uppercase title shown above the list. */
  title?: ReactNode;
  /** Text shown when no items match. */
  emptyText?: ReactNode;
  /** Whether to internally filter `items` by `query`. Defaults to true. */
  filter?: boolean;
  /** Called when an item is selected. */
  onSelect?: (item: CommandMenuItem) => void;
  className?: string;
};

function itemHaystack(item: CommandMenuItem): string {
  const labelText = typeof item.label === "string" ? item.label : "";
  return `${labelText} ${item.keywords ?? ""}`.toLowerCase();
}

/**
 * Searchable command / option palette. Renders a floating-style surface with a
 * list of icon + label + description rows and optional status badges. Filtering
 * is internal by default; pass `filter={false}` to control the list yourself.
 *
 * The component is position-agnostic — wrap it (or pass `className`) to place it
 * as a popover above an input, in a dropdown, etc.
 */
export function CommandMenu({
  items,
  query = "",
  title,
  emptyText = "No results found",
  filter = true,
  onSelect,
  className
}: CommandMenuProps) {
  const normalizedQuery = query.trim().toLowerCase();
  const visible = filter && normalizedQuery
    ? items.filter((item) => itemHaystack(item).includes(normalizedQuery))
    : items;

  return (
    <div
      role="listbox"
      className={cn(
        "bfrs-overflow-hidden bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-menu",
        className
      )}
    >
      {title && (
        <div className="bfrs-border-b bfrs-border-border-light bfrs-px-3 bfrs-pb-1.5 bfrs-pt-2">
          <span className="bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.07em] bfrs-text-text-muted">{title}</span>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bfrs-px-3.5 bfrs-py-4 bfrs-text-center bfrs-text-xs bfrs-text-text-muted">{emptyText}</div>
      ) : (
        visible.map((item) => (
          <button
            key={item.id}
            type="button"
            role="option"
            disabled={item.disabled}
            onClick={() => onSelect?.(item)}
            className="bfrs-flex bfrs-w-full bfrs-items-center bfrs-gap-2.5 bfrs-px-3.5 bfrs-py-2.5 bfrs-text-left bfrs-transition-colors hover:bfrs-bg-surface-muted disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50"
          >
            {item.icon && (
              <span className="bfrs-flex bfrs-size-[30px] bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-lg bfrs-bg-primary-soft bfrs-text-primary">
                {item.icon}
              </span>
            )}
            <span className="bfrs-min-w-0 bfrs-flex-1">
              <span className="bfrs-block bfrs-truncate bfrs-text-[12.5px] bfrs-font-medium bfrs-text-text-primary">{item.label}</span>
              {item.description && (
                <span className="bfrs-block bfrs-truncate bfrs-text-[11px] bfrs-text-text-muted">{item.description}</span>
              )}
            </span>
            {item.status && (
              <Badge size="xs" tone={item.status.tone ?? "neutral"} className="bfrs-shrink-0">
                {item.status.label}
              </Badge>
            )}
          </button>
        ))
      )}
    </div>
  );
}

CommandMenu.displayName = "CommandMenu";
